/**
 * DocumentComplianceEngine - Generic Document Compliance & Requirements Engine
 *
 * A reusable, configurable engine for evaluating document compliance against
 * a set of rules. Can be used in any project requiring document management
 * with compliance tracking.
 *
 * USAGE:
 * ```javascript
 * import { DocumentComplianceEngine } from './lib/DocumentComplianceEngine';
 *
 * // Load rules from JSON, database, or API
 * const rules = await fetch('/api/document-rules').then(r => r.json());
 *
 * // Create engine instance
 * const engine = new DocumentComplianceEngine(rules);
 *
 * // Evaluate a single entity
 * const report = engine.evaluateEntity('VEHICLE', 'VHC_000001', documents, vehicleData);
 * console.log(report.completeness); // 0.75 (75%)
 * console.log(report.folders); // Array of folder statuses
 * ```
 *
 * RULE SCHEMA:
 * ```json
 * {
 *   "scope": "VEHICLE",
 *   "folderCode": "10_REGISTRATION",
 *   "label": "Immatriculation",
 *   "description": "Carte grise, certificat d'immatriculation",
 *   "required": true,
 *   "conditionalOn": null,
 *   "renewable": false,
 *   "renewalWarningDays": 30,
 *   "minDocuments": 1,
 *   "validityRequired": false,
 *   "order": 10
 * }
 * ```
 *
 * @version 1.0.0
 * @license MIT
 */

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Folder status codes
 */
export const FOLDER_STATUS = {
  COMPLETE: 'complete',           // Required docs present & valid
  MISSING_REQUIRED: 'missing',    // Mandatory doc missing
  EXPIRING_SOON: 'expiring',      // Valid but expires soon
  EXPIRED: 'expired',             // Document expired
  OPTIONAL_EMPTY: 'empty',        // Optional folder, no docs
  HAS_DOCUMENTS: 'has_docs',      // Has documents (non-required folder)
  NOT_APPLICABLE: 'not_applicable', // Conditional folder, condition not met
  // Validation workflow states
  PENDING_VALIDATION: 'pending_validation',   // Doc uploaded, awaiting human validation
  VALIDATION_REJECTED: 'validation_rejected', // Doc rejected, replacement needed
  EXCEPTION_RECORDED: 'exception_recorded'    // Cannot provide with justification
};

/**
 * Default status labels (French)
 */
export const DEFAULT_STATUS_LABELS = {
  [FOLDER_STATUS.COMPLETE]: { label: 'Complet', icon: 'CheckCircle', color: 'green' },
  [FOLDER_STATUS.MISSING_REQUIRED]: { label: 'Requis manquant', icon: 'AlertTriangle', color: 'red' },
  [FOLDER_STATUS.EXPIRING_SOON]: { label: 'Expire bientot', icon: 'Clock', color: 'orange' },
  [FOLDER_STATUS.EXPIRED]: { label: 'Expire', icon: 'XCircle', color: 'red' },
  [FOLDER_STATUS.OPTIONAL_EMPTY]: { label: 'Pas de document', icon: 'Minus', color: 'gray' },
  [FOLDER_STATUS.HAS_DOCUMENTS]: { label: 'documents', icon: 'FileText', color: 'blue' },
  [FOLDER_STATUS.NOT_APPLICABLE]: { label: 'Non applicable', icon: 'Slash', color: 'gray' },
  // Validation workflow states
  [FOLDER_STATUS.PENDING_VALIDATION]: { label: 'En attente de validation', icon: 'Clock', color: 'amber' },
  [FOLDER_STATUS.VALIDATION_REJECTED]: { label: 'Rejeté', icon: 'XCircle', color: 'red' },
  [FOLDER_STATUS.EXCEPTION_RECORDED]: { label: 'Exception enregistrée', icon: 'AlertTriangle', color: 'yellow' }
};

/**
 * Default renewal warning thresholds (days)
 */
export const DEFAULT_WARNING_THRESHOLDS = {
  critical: 30,   // Red warning
  warning: 60,    // Orange warning
  notice: 90      // Yellow notice
};

// =============================================================================
// COMPLIANCE ENGINE CLASS
// =============================================================================

export class DocumentComplianceEngine {
  /**
   * Create a new DocumentComplianceEngine
   *
   * @param {Object} config - Configuration object
   * @param {Array} config.rules - Array of folder rules
   * @param {Object} [config.statusLabels] - Custom status labels
   * @param {Object} [config.warningThresholds] - Custom warning thresholds
   * @param {Function} [config.getDocumentExpiry] - Custom function to extract expiry from doc
   */
  constructor(config = {}) {
    this.rules = config.rules || [];
    this.statusLabels = { ...DEFAULT_STATUS_LABELS, ...config.statusLabels };
    this.warningThresholds = { ...DEFAULT_WARNING_THRESHOLDS, ...config.warningThresholds };
    this.getDocumentExpiry = config.getDocumentExpiry || ((doc) => doc.valid_to);

    // Index rules by scope for fast lookup
    this._rulesByScope = {};
    this.rules.forEach(rule => {
      if (!this._rulesByScope[rule.scope]) {
        this._rulesByScope[rule.scope] = [];
      }
      this._rulesByScope[rule.scope].push(rule);
    });
  }

  /**
   * Get rules for a specific scope
   * @param {string} scope - The scope (e.g., 'VEHICLE', 'DRIVER')
   * @returns {Array} Array of rules for the scope
   */
  getRulesForScope(scope) {
    return (this._rulesByScope[scope] || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  /**
   * Check if a conditional rule applies based on entity data
   *
   * @param {Object} rule - The folder rule
   * @param {Object} entityData - The entity data to check against
   * @returns {boolean} True if the rule applies
   */
  checkCondition(rule, entityData) {
    if (!rule.conditionalOn) {
      return true; // No condition, always applies
    }

    const { field, operator = 'in', values } = rule.conditionalOn;
    const entityValue = entityData?.[field];

    switch (operator) {
      case 'in':
        return values.includes(entityValue);
      case 'not_in':
        return !values.includes(entityValue);
      case 'equals':
        return entityValue === values;
      case 'not_equals':
        return entityValue !== values;
      case 'exists':
        return entityValue !== null && entityValue !== undefined;
      case 'not_exists':
        return entityValue === null || entityValue === undefined;
      case 'gt':
        return entityValue > values;
      case 'gte':
        return entityValue >= values;
      case 'lt':
        return entityValue < values;
      case 'lte':
        return entityValue <= values;
      default:
        return true;
    }
  }

  /**
   * Calculate days until expiry for a document
   *
   * @param {Object} doc - Document object
   * @returns {number|null} Days until expiry (negative if expired), null if no expiry
   */
  getDaysUntilExpiry(doc) {
    const expiryDate = this.getDocumentExpiry(doc);
    if (!expiryDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);

    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  }

  /**
   * Get the most critical expiry status from a set of documents
   *
   * @param {Array} docs - Array of documents
   * @param {number} warningDays - Days threshold for warning
   * @returns {Object} { status, daysUntilExpiry, expiringDoc }
   */
  getExpiryStatus(docs, warningDays = 30) {
    let mostCritical = null;

    for (const doc of docs) {
      const days = this.getDaysUntilExpiry(doc);
      if (days === null) continue;

      if (mostCritical === null || days < mostCritical.daysUntilExpiry) {
        mostCritical = { daysUntilExpiry: days, expiringDoc: doc };
      }
    }

    if (!mostCritical) {
      return { status: null, daysUntilExpiry: null, expiringDoc: null };
    }

    const { daysUntilExpiry, expiringDoc } = mostCritical;

    if (daysUntilExpiry < 0) {
      return { status: FOLDER_STATUS.EXPIRED, daysUntilExpiry, expiringDoc };
    }
    if (daysUntilExpiry <= warningDays) {
      return { status: FOLDER_STATUS.EXPIRING_SOON, daysUntilExpiry, expiringDoc };
    }

    return { status: null, daysUntilExpiry, expiringDoc: null };
  }

  /**
   * Get aggregated validation status from documents
   * Priority: rejected > pending > cannot_provide > validated
   *
   * @param {Array} docs - Array of documents
   * @returns {Object} { status, pendingCount, rejectedCount, exceptionCount, validatedCount }
   */
  getDocumentValidationStatus(docs) {
    let pendingCount = 0;
    let rejectedCount = 0;
    let exceptionCount = 0;
    let validatedCount = 0;
    let noValidationCount = 0;

    for (const doc of docs) {
      const vs = doc.validation_status;
      if (vs === 'rejected') rejectedCount++;
      else if (vs === 'pending') pendingCount++;
      else if (vs === 'cannot_provide') exceptionCount++;
      else if (vs === 'validated') validatedCount++;
      else noValidationCount++; // NULL = validation not required for this doc
    }

    // Determine aggregate status (most critical first)
    let status = null;
    if (rejectedCount > 0) status = 'rejected';
    else if (pendingCount > 0) status = 'pending';
    else if (exceptionCount > 0) status = 'cannot_provide';
    else if (validatedCount > 0) status = 'validated';

    return { status, pendingCount, rejectedCount, exceptionCount, validatedCount, noValidationCount };
  }

  /**
   * Evaluate a single folder against its rule
   *
   * @param {Object} rule - The folder rule
   * @param {Array} documents - Documents in this folder
   * @param {Object} entityData - Entity data for conditional checks
   * @returns {Object} Folder evaluation result
   */
  evaluateFolder(rule, documents = [], entityData = {}) {
    const docs = documents || [];
    const docCount = docs.length;

    // Check if condition applies
    const conditionMet = this.checkCondition(rule, entityData);
    if (!conditionMet) {
      return {
        folderCode: rule.folderCode,
        label: rule.label,
        description: rule.description,
        status: FOLDER_STATUS.NOT_APPLICABLE,
        statusInfo: this.statusLabels[FOLDER_STATUS.NOT_APPLICABLE],
        documentCount: 0,
        documents: [],
        required: false,
        conditionMet: false,
        completeness: 1, // N/A folders count as complete
        issues: [],
        validationInfo: null
      };
    }

    const issues = [];
    let status;
    let expiryInfo = { daysUntilExpiry: null, expiringDoc: null };
    let validationInfo = null;

    // Check document count requirements
    const minDocs = rule.minDocuments || (rule.required ? 1 : 0);

    if (docCount === 0) {
      // No documents
      if (rule.required) {
        status = FOLDER_STATUS.MISSING_REQUIRED;
        issues.push({ type: 'missing', message: `Document requis manquant` });
      } else {
        status = FOLDER_STATUS.OPTIONAL_EMPTY;
      }
    } else {
      // Has documents - check validation status first (if rule requires validation)
      if (rule.requiresValidation) {
        validationInfo = this.getDocumentValidationStatus(docs);

        if (validationInfo.status === 'rejected') {
          status = FOLDER_STATUS.VALIDATION_REJECTED;
          issues.push({
            type: 'validation_rejected',
            message: `Document rejeté, remplacement requis`,
            rejectedCount: validationInfo.rejectedCount
          });
        } else if (validationInfo.status === 'pending') {
          status = FOLDER_STATUS.PENDING_VALIDATION;
          issues.push({
            type: 'pending_validation',
            message: `${validationInfo.pendingCount} document(s) en attente de validation`,
            pendingCount: validationInfo.pendingCount
          });
        } else if (validationInfo.status === 'cannot_provide') {
          status = FOLDER_STATUS.EXCEPTION_RECORDED;
          issues.push({
            type: 'exception_recorded',
            message: `Exception enregistrée`,
            exceptionCount: validationInfo.exceptionCount
          });
        }
        // If validated or no validation required, continue to check expiry
      }

      // Only check expiry if not already set by validation status (or validation passed)
      if (!status) {
        if (rule.renewable) {
          const warningDays = rule.renewalWarningDays || this.warningThresholds.warning;
          expiryInfo = this.getExpiryStatus(docs, warningDays);

          if (expiryInfo.status === FOLDER_STATUS.EXPIRED) {
            status = FOLDER_STATUS.EXPIRED;
            issues.push({
              type: 'expired',
              message: `Expire depuis ${Math.abs(expiryInfo.daysUntilExpiry)} jour(s)`,
              document: expiryInfo.expiringDoc
            });
          } else if (expiryInfo.status === FOLDER_STATUS.EXPIRING_SOON) {
            status = FOLDER_STATUS.EXPIRING_SOON;
            issues.push({
              type: 'expiring',
              message: `Expire dans ${expiryInfo.daysUntilExpiry} jour(s)`,
              document: expiryInfo.expiringDoc
            });
          } else {
            status = rule.required ? FOLDER_STATUS.COMPLETE : FOLDER_STATUS.HAS_DOCUMENTS;
          }
        } else {
          // Not renewable, just check count
          if (docCount < minDocs) {
            status = FOLDER_STATUS.MISSING_REQUIRED;
            issues.push({
              type: 'insufficient',
              message: `${minDocs - docCount} document(s) manquant(s)`
            });
          } else {
            status = rule.required ? FOLDER_STATUS.COMPLETE : FOLDER_STATUS.HAS_DOCUMENTS;
          }
        }
      }
    }

    // Calculate folder completeness with validation status weights
    let completeness = 0;
    if (status === FOLDER_STATUS.COMPLETE || status === FOLDER_STATUS.HAS_DOCUMENTS) {
      completeness = 1;
    } else if (status === FOLDER_STATUS.EXPIRING_SOON) {
      completeness = 0.8; // Almost complete but needs attention
    } else if (status === FOLDER_STATUS.PENDING_VALIDATION) {
      completeness = 0.8; // Has doc but awaiting validation
    } else if (status === FOLDER_STATUS.EXCEPTION_RECORDED) {
      completeness = 0.7; // Acknowledged gap with justification
    } else if (status === FOLDER_STATUS.EXPIRED) {
      completeness = 0.3; // Has doc but expired
    } else if (status === FOLDER_STATUS.VALIDATION_REJECTED) {
      completeness = 0.3; // Doc rejected, needs replacement
    } else if (status === FOLDER_STATUS.OPTIONAL_EMPTY || status === FOLDER_STATUS.NOT_APPLICABLE) {
      completeness = 1; // Optional/N/A folders don't affect completeness
    }

    return {
      folderCode: rule.folderCode,
      label: rule.label,
      description: rule.description,
      status,
      statusInfo: this.statusLabels[status],
      documentCount: docCount,
      documents: docs,
      required: rule.required,
      renewable: rule.renewable,
      requiresValidation: rule.requiresValidation || false,
      conditionMet: true,
      completeness,
      issues,
      daysUntilExpiry: expiryInfo.daysUntilExpiry,
      validationInfo,
      order: rule.order || 0,
      color: rule.color
    };
  }

  /**
   * Evaluate all folders for an entity
   *
   * @param {string} scope - The scope (e.g., 'VEHICLE')
   * @param {string} entityId - The entity reference ID
   * @param {Array} allDocuments - All documents (will be filtered by entityId)
   * @param {Object} entityData - Entity data for conditional checks
   * @returns {Object} Complete evaluation report
   */
  evaluateEntity(scope, entityId, allDocuments = [], entityData = {}) {
    const rules = this.getRulesForScope(scope);

    // Filter documents for this entity
    const entityDocs = allDocuments.filter(doc =>
      doc.reference_id === entityId && doc.scope === scope
    );

    // Group documents by folder
    const docsByFolder = {};
    entityDocs.forEach(doc => {
      const folderCode = this._getDocumentFolder(scope, doc.document_type);
      if (!docsByFolder[folderCode]) {
        docsByFolder[folderCode] = [];
      }
      docsByFolder[folderCode].push(doc);
    });

    // Evaluate each folder
    const folderResults = rules.map(rule => {
      const folderDocs = docsByFolder[rule.folderCode] || [];
      return this.evaluateFolder(rule, folderDocs, entityData);
    });

    // Calculate overall completeness (only required folders count)
    const requiredFolders = folderResults.filter(f => f.required && f.conditionMet);
    const completeFolders = requiredFolders.filter(f =>
      f.status === FOLDER_STATUS.COMPLETE ||
      f.status === FOLDER_STATUS.EXPIRING_SOON
    );

    const completeness = requiredFolders.length > 0
      ? completeFolders.length / requiredFolders.length
      : 1;

    // Collect all issues
    const allIssues = folderResults.flatMap(f =>
      f.issues.map(issue => ({ ...issue, folder: f.folderCode, folderLabel: f.label }))
    );

    // Count by status
    const statusCounts = {};
    Object.values(FOLDER_STATUS).forEach(s => statusCounts[s] = 0);
    folderResults.forEach(f => statusCounts[f.status]++);

    return {
      scope,
      entityId,
      entityData,
      folders: folderResults.sort((a, b) => a.order - b.order),
      completeness,
      completenessPercent: Math.round(completeness * 100),
      totalDocuments: entityDocs.length,
      statusCounts,
      issues: allIssues,
      hasIssues: allIssues.length > 0,
      hasCriticalIssues: allIssues.some(i => i.type === 'missing' || i.type === 'expired')
    };
  }

  /**
   * Evaluate multiple entities at once
   *
   * @param {string} scope - The scope
   * @param {Array} entities - Array of { id, data } objects
   * @param {Array} allDocuments - All documents
   * @returns {Array} Array of evaluation reports
   */
  evaluateEntities(scope, entities, allDocuments = []) {
    return entities.map(entity =>
      this.evaluateEntity(scope, entity.id, allDocuments, entity.data || {})
    );
  }

  /**
   * Get folder code for a document type
   * @private
   */
  _getDocumentFolder(scope, documentType) {
    const rules = this.getRulesForScope(scope);
    for (const rule of rules) {
      if (rule.documentTypes?.includes(documentType)) {
        return rule.folderCode;
      }
    }
    // Default to last folder (usually MISC)
    const lastRule = rules[rules.length - 1];
    return lastRule?.folderCode || '90_MISC';
  }

  /**
   * Generate a summary report for multiple entities
   *
   * @param {Array} reports - Array of entity evaluation reports
   * @returns {Object} Summary statistics
   */
  generateSummary(reports) {
    const total = reports.length;
    const complete = reports.filter(r => r.completeness === 1).length;
    const withIssues = reports.filter(r => r.hasIssues).length;
    const critical = reports.filter(r => r.hasCriticalIssues).length;

    const avgCompleteness = total > 0
      ? reports.reduce((sum, r) => sum + r.completeness, 0) / total
      : 0;

    const allIssues = reports.flatMap(r =>
      r.issues.map(i => ({ ...i, entityId: r.entityId }))
    );

    const issuesByType = {};
    allIssues.forEach(issue => {
      if (!issuesByType[issue.type]) {
        issuesByType[issue.type] = [];
      }
      issuesByType[issue.type].push(issue);
    });

    return {
      totalEntities: total,
      completeEntities: complete,
      entitiesWithIssues: withIssues,
      criticalIssues: critical,
      averageCompleteness: avgCompleteness,
      averageCompletenessPercent: Math.round(avgCompleteness * 100),
      totalIssues: allIssues.length,
      issuesByType
    };
  }
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

/**
 * Create a DocumentComplianceEngine with rules from various sources
 *
 * @param {Object|Array|string} rulesSource - Rules array, config object, or URL
 * @param {Object} options - Additional options
 * @returns {Promise<DocumentComplianceEngine>} Engine instance
 */
export async function createComplianceEngine(rulesSource, options = {}) {
  let rules;

  if (Array.isArray(rulesSource)) {
    rules = rulesSource;
  } else if (typeof rulesSource === 'string') {
    // Assume it's a URL
    const response = await fetch(rulesSource);
    const data = await response.json();
    rules = data.rules || data;
  } else if (rulesSource && typeof rulesSource === 'object') {
    rules = rulesSource.rules || [];
  } else {
    rules = [];
  }

  return new DocumentComplianceEngine({ rules, ...options });
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Convert folder structure to rules format
 * Helper to migrate from old FOLDER_STRUCTURE format to rules
 *
 * @param {Object} folderStructure - Old FOLDER_STRUCTURE object
 * @param {Object} requirementOverrides - Override requirements per folder
 * @returns {Array} Rules array
 */
export function convertFolderStructureToRules(folderStructure, requirementOverrides = {}) {
  const rules = [];

  Object.entries(folderStructure).forEach(([scope, folders]) => {
    folders.forEach(folder => {
      if (folder.hidden) return; // Skip hidden folders

      const overrides = requirementOverrides[`${scope}.${folder.code}`] || {};

      rules.push({
        scope,
        folderCode: folder.code,
        label: folder.label,
        description: folder.description,
        documentTypes: folder.documentTypes,
        order: folder.order,
        color: folder.color,
        required: overrides.required ?? false,
        renewable: overrides.renewable ?? false,
        renewalWarningDays: overrides.renewalWarningDays ?? 30,
        minDocuments: overrides.minDocuments ?? (overrides.required ? 1 : 0),
        conditionalOn: overrides.conditionalOn ?? null,
        requiresValidation: overrides.requiresValidation ?? false
      });
    });
  });

  return rules;
}

export default DocumentComplianceEngine;
